import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { tap, switchMap, map, catchError } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { PlugpointEnvironment } from '../PlugpointEnvironment';

export interface RoleAccessResponse {
  userName: string;
  role: string;
  access: string;
}

@Injectable({ providedIn: 'root' })
export class GatewayAuthService {
  private readonly gatewayBaseUrl = `${PlugpointEnvironment.url}/gateway`;
  /**
   * IMPORTANT: Set this to the Auth0 API Audience configured for the gateway.
   * This is NOT a token; it is the audience identifier used when requesting the JWT.
   * Example assumption: 'https://t2.plugpoint.ro/gateway' or a custom identifier.
   */
  private readonly gatewayAudience = 'https://plugway.eu.auth0.com/api/v2/';

  private roleAccessSubject = new BehaviorSubject<RoleAccessResponse | null>(null);
  roleAccess$ = this.roleAccessSubject.asObservable();

  constructor(private http: HttpClient, private auth: AuthService) {}

  /**
   * Orchestrates the Test JWT flow:
   * - Ensures user is logged in
   * - Retrieves an Auth0 JWT for the gateway audience
   * - Calls /api/user/{userName} and stores role/access
   * - If role === 'god', calls /api/god-data and logs the response
   */
  testJwt(): void {
    // Ensure user is logged in
    if (!this.auth.loggedIn) {
      this.auth.login();
      return; // After redirect/login, user can press the button again
    }

    this.getUser$Once().pipe(
      map(user => {
        if (!user || !user.sub) {
          throw new Error('Auth0 user not available; try again after login.');
        }
        return this.stripProviderPrefix(user.sub);
      }),
      switchMap((userName: string) => this.getGatewayToken$().pipe(
        switchMap((token: string) => this.fetchUserRoleAccess(token, userName).pipe(
          tap((roleAccess) => {
            this.roleAccessSubject.next(roleAccess);
          }),
          switchMap((roleAccess) => {
            if (roleAccess && roleAccess.role === 'god') {
              return this.fetchGodData(token).pipe(
                tap(godData => console.log('god-data response:', godData)),
                map(() => roleAccess)
              );
            }
            return of(roleAccess);
          })
        ))
      )),
      catchError(err => {
        console.error('Test JWT flow error:', err);
        return of(null);
      })
    ).subscribe();
  }

  /** Retrieves the Auth0 user once. */
  private getUser$Once(): Observable<any> {
    return this.auth.getUser$();
  }

  /** Removes provider prefix (e.g., 'google-oauth2|123' -> '123'). */
  private stripProviderPrefix(sub: string): string {
    const idx = sub.indexOf('|');
    return idx >= 0 ? sub.substring(idx + 1) : sub;
  }

  /** Gets a JWT for the gateway API using Auth0 SDK without hardcoding tokens. */
  getGatewayToken$(): Observable<string> {
    return this.getAuth0Client$().pipe(
      switchMap((client: any) => from(client.getTokenSilently({
        audience: this.gatewayAudience,
      })) as Observable<string>),
      catchError(err => {
        console.error('getTokenSilently error:', err);
        return of('');
      })
    );
  }

  private getAuth0Client$(): Observable<any> {
    return this.auth.auth0Client$;
  }

  /** GET /api/user/{userName} */
  private fetchUserRoleAccess(token: string, userName: string): Observable<RoleAccessResponse> {
    const url = `${this.gatewayBaseUrl}/api/user/${encodeURIComponent(userName)}`;
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    return this.http.get<RoleAccessResponse>(url, { headers }).pipe(
      tap(res => {
        // eslint-disable-next-line no-console
        console.log('gateway role/access:', res);
      })
    );
  }

  /** GET /api/god-data (only if role === 'god') */
  private fetchGodData(token: string): Observable<any> {
    const url = `${this.gatewayBaseUrl}/api/god-data`;
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    return this.http.get<any>(url, { headers });
  }
}
